import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Banknote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  tiersWithPriceDeltas,
  type SupplyPriceTier,
} from '@/utils/supplyPriceTiers';

interface SupplyPriceTiersHintProps {
  tiers: SupplyPriceTier[] | undefined;
  currencySymbol: string;
  unitLabel?: string;
}

/**
 * Icono junto al precio (SUP-ORD-008): lista tramos y si sube/baja el unitario.
 * Solo se muestra si hay ≥2 tramos. Tooltip en portal (fixed) para no cortarse
 * por overflow de la fila/tabla; preferencia arriba del icono.
 */
export function SupplyPriceTiersHint({
  tiers,
  currencySymbol,
  unitLabel = '',
}: SupplyPriceTiersHintProps) {
  const { t, i18n } = useTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const rows = useMemo(() => tiersWithPriceDeltas(tiers ?? []), [tiers]);

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    // Anclar arriba-centro del icono; el panel usa translateY(-100%).
    let left = rect.left + rect.width / 2;
    const panelHalf = 140;
    left = Math.min(
      Math.max(left, margin + panelHalf),
      window.innerWidth - margin - panelHalf,
    );
    setPos({
      top: Math.max(rect.top - 6, margin),
      left,
    });
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setOpen(true);
  }, [updatePosition]);

  const hide = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, updatePosition]);

  if (rows.length < 2) {
    return null;
  }

  const formatQty = (n: number) =>
    n.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  const formatPrice = (n: number) =>
    n.toLocaleString(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });

  return (
    <div
      ref={anchorRef}
      className="inline-flex shrink-0"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <button
        type="button"
        className="inline-flex cursor-help rounded p-0.5 text-emerald-600/80 outline-none hover:text-emerald-700 focus-visible:ring-2 focus-visible:ring-[#1681ba] focus-visible:ring-offset-1"
        aria-label={t('supply.table.priceTiersHintAria')}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Banknote className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[9999] w-max max-w-[min(18rem,calc(100vw-1rem))] -translate-x-1/2 -translate-y-full"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="rounded-md border border-gray-200 bg-white px-2.5 py-2 text-left text-[11px] leading-snug text-gray-700 shadow-lg ring-1 ring-black/5">
              <p className="mb-1.5 font-medium text-gray-900">
                {t('supply.table.priceTiersHintTitle')}
              </p>
              <ul className="space-y-1">
                {rows.map((row) => {
                  const range =
                    row.quantity_to == null
                      ? t('supply.table.priceTiersRangeOpen', {
                          from: formatQty(row.quantity_from),
                          unit: unitLabel,
                        })
                      : t('supply.table.priceTiersRangeClosed', {
                          from: formatQty(row.quantity_from),
                          to: formatQty(row.quantity_to),
                          unit: unitLabel,
                        });
                  let deltaLabel: string | null = null;
                  let deltaClass = 'text-gray-500';
                  if (row.deltaFromPrevious != null && row.changeKind !== 'same') {
                    const abs = Math.abs(row.deltaFromPrevious);
                    if (row.changeKind === 'discount') {
                      deltaLabel = t('supply.table.priceTiersDeltaDiscount', {
                        currencySymbol,
                        amount: formatPrice(abs),
                      });
                      deltaClass = 'text-green-700';
                    } else {
                      deltaLabel = t('supply.table.priceTiersDeltaIncrease', {
                        currencySymbol,
                        amount: formatPrice(abs),
                      });
                      deltaClass = 'text-amber-700';
                    }
                  }
                  return (
                    <li key={`${row.quantity_from}-${row.quantity_to ?? 'open'}`}>
                      <span className="text-gray-600">{range}</span>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className="font-medium tabular-nums text-gray-900">
                        {currencySymbol}
                        {formatPrice(row.final_price)}
                      </span>
                      {deltaLabel && (
                        <span className={`ml-1 tabular-nums ${deltaClass}`}>
                          ({deltaLabel})
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-white drop-shadow-sm" />
          </div>,
          document.body,
        )}
    </div>
  );
}
